/**
 * What the app-wide template list downloads, and how a list entry gets its full row later.
 *
 * The list (AppContext `state.templates`) is loaded on every page, so it selects only the small
 * columns. `content` (document HTML, or a deck's slide JSON with copies of the original slide
 * text) and the large jsonb columns (`source_document`, `generation_metadata`) are loaded when a
 * template is opened or used: `actions.loadTemplate(id)` for one full row, or
 * `actions.loadTemplateContents(ids)` for the content of several (quality scores, thumbnails).
 *
 * A list entry has `contentLoaded: false` until then; read it through
 * {@link isTemplateContentLoaded} so objects built elsewhere (tests, older callers) that carry
 * `content` but no flag still count as loaded.
 */

/** Columns of the template list (no `content`, `source_document` or `generation_metadata`). */
export const TEMPLATE_LIST_COLUMNS = Object.freeze([
  'id',
  'name',
  'description',
  'template_type',
  'status',
  'page_setup',
  'created_at',
  'updated_at',
  'user_id',
  'account_id',
]);

/** Large columns the list never downloads. */
export const TEMPLATE_HEAVY_COLUMNS = Object.freeze(['content', 'source_document', 'generation_metadata']);

/** `select()` string for the template list. */
export const TEMPLATE_LIST_SELECT = TEMPLATE_LIST_COLUMNS.join(',');

/** The list select for a database without the page_setup column (migration 20261105000100 not applied). */
export const TEMPLATE_LIST_SELECT_LEGACY = TEMPLATE_LIST_COLUMNS.filter((c) => c !== 'page_setup').join(',');

/** List columns plus `content`: what a batch content load selects (enough to open a template). */
export const TEMPLATE_CONTENT_SELECT = `${TEMPLATE_LIST_SELECT},content`;

/** {@link TEMPLATE_CONTENT_SELECT} without page_setup. */
export const TEMPLATE_CONTENT_SELECT_LEGACY = `${TEMPLATE_LIST_SELECT_LEGACY},content`;

/** Largest number of ids in one `in()` filter (keeps the request URL short). */
export const TEMPLATE_CONTENT_BATCH_SIZE = 50;

/** Fields of the local template shape that come from the large columns. */
export const TEMPLATE_HEAVY_FIELDS = Object.freeze(['content', 'sourceDocument', 'generationMetadata']);

/**
 * Whether a local template's `content` is the stored content (not a list entry still waiting for it).
 * @param {object|null|undefined} template - Local template.
 * @returns {boolean} True when `content` can be used.
 */
export function isTemplateContentLoaded(template) {
  return Boolean(template) && template.contentLoaded !== false;
}

/**
 * Ids of the templates whose content is not loaded yet.
 * @param {Array<object>} templates - Local templates.
 * @param {(template: object) => boolean} [include] - Only these templates.
 * @returns {string[]} Ids, sorted (stable for effect dependencies).
 */
export function templateIdsMissingContent(templates, include) {
  return (templates || [])
    .filter((t) => t && t.id && !isTemplateContentLoaded(t) && (!include || include(t)))
    .map((t) => t.id)
    .sort();
}

/**
 * Time value of an updated_at string (NaN-safe: unparseable values sort first).
 * @param {string|null|undefined} value - Timestamp.
 * @returns {number} Milliseconds.
 */
function timeOf(value) {
  const parsed = Date.parse(value || '');
  return Number.isNaN(parsed) ? -Infinity : parsed;
}

/**
 * Merge freshly read template rows (already mapped to the local shape) into the list.
 *
 * - A row replaces the entry with the same id, except when the entry already has its content and
 *   is newer (a read that started before a save must not undo the save).
 * - A row without content (a list row) keeps the entry's loaded content when both have the same
 *   `updatedAt`; when the template changed since, the content is dropped and reloaded on use.
 * - Rows not in the list are added at the front (newest first) when `addMissing` is set.
 *
 * @param {Array<object>} current - Current local templates.
 * @param {Array<object>} incoming - Mapped rows.
 * @param {{addMissing?: boolean}} [options] - Options.
 * @returns {Array<object>} Next list (the same array when nothing changed).
 */
export function mergeTemplateRows(current, incoming, { addMissing = false } = {}) {
  const list = Array.isArray(current) ? current : [];
  const byId = new Map((incoming || []).filter((row) => row && row.id).map((row) => [row.id, row]));
  if (byId.size === 0) return list;
  let changed = false;
  const next = list.map((existing) => {
    const row = byId.get(existing.id);
    if (!row) return existing;
    byId.delete(existing.id);
    const merged = mergeOne(existing, row);
    if (merged !== existing) changed = true;
    return merged;
  });
  if (addMissing && byId.size > 0) {
    changed = true;
    return [...byId.values(), ...next];
  }
  return changed ? next : list;
}

/**
 * A reloaded list replaces the current one (same order, deleted templates gone), keeping content
 * already loaded for templates that did not change since (see {@link mergeTemplateRows}).
 * @param {Array<object>} current - Current local templates.
 * @param {Array<object>} rows - The reloaded list, mapped.
 * @returns {Array<object>} Next list.
 */
export function replaceTemplateList(current, rows) {
  const previous = new Map((Array.isArray(current) ? current : []).map((t) => [t.id, t]));
  return (rows || []).map((row) => {
    const existing = previous.get(row.id);
    return existing ? mergeOne(existing, row) : row;
  });
}

/**
 * Merge one mapped row into one existing entry.
 * @param {object} existing - Current entry.
 * @param {object} row - Incoming mapped row.
 * @returns {object} Entry to keep.
 */
function mergeOne(existing, row) {
  const existingLoaded = isTemplateContentLoaded(existing);
  if (row.contentLoaded) {
    if (existingLoaded && timeOf(existing.updatedAt) > timeOf(row.updatedAt)) return existing;
    return row;
  }
  // Row without content.
  if (existingLoaded && existing.updatedAt === row.updatedAt) {
    const kept = { ...row, contentLoaded: true };
    TEMPLATE_HEAVY_FIELDS.forEach((field) => {
      if (existing[field] !== undefined) kept[field] = existing[field];
    });
    return kept;
  }
  return row;
}

/** Template statuses of a PowerPoint import that has not produced a usable template yet. */
export const TEMPLATE_IMPORT_PENDING_STATUSES = Object.freeze(['importing', 'processing', 'failed']);

/**
 * Whether a template can be used to generate documents: an import still running (or failed)
 * only has placeholder content, and the worker refuses it.
 * @param {object|null|undefined} template - Template list entry.
 * @returns {boolean} True when it can be picked for generation.
 */
export function isTemplateUsableForGeneration(template) {
  return Boolean(template) && !TEMPLATE_IMPORT_PENDING_STATUSES.includes(template.status);
}
